import { Camera, CreditCard, QrCode, Upload, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";
import AppConstants from "../../configs/AppConstants";
import { PublicPortalVerificationMethodsSkeleton } from "./PublicPortalSkeleton";

const methodIcons = {
  scan: QrCode,
  upload: Upload,
  id: CreditCard,
};

const VerificationMethods = ({
  captchaRequired,
  isLoading = false,
  methods,
  onCredentialSubmit,
  onFileSelected,
  onMethodSelect,
  resetKey,
}) => {
  const fileInputRef = useRef(null);
  const captchaTokenRef = useRef(null);
  const recaptchaRef = useRef(null);
  const pendingFileRef = useRef(null);
  const pendingCredentialRef = useRef(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [pendingMethod, setPendingMethod] = useState(null);
  const [credentialId, setCredentialId] = useState("");
  const [showCredentialInput, setShowCredentialInput] = useState(false);
  const { t } = useTranslation("publicPortal");

  const clearCaptchaState = () => {
    captchaTokenRef.current = null;
    pendingFileRef.current = null;
    pendingCredentialRef.current = null;
    recaptchaRef.current?.reset();
    setPendingMethod(null);
    setShowCaptcha(false);
  };

  useEffect(() => {
    clearCaptchaState();
    setCredentialId("");
    setShowCredentialInput(false);
  }, [resetKey]);

  const handleMethodClick = (methodId) => {
    if (methodId === "upload") {
      fileInputRef.current?.click();
      return;
    }
    if (methodId === "credential") {
      setShowCredentialInput(true);
      return;
    }
    if (captchaRequired) {
      setPendingMethod(methodId);
      setShowCaptcha(true);
      return;
    }
    onMethodSelect(methodId, null);
  };

  const handleCaptchaChange = (token) => {
    if (!token || !pendingMethod) return;
    const method = pendingMethod;
    const pendingFile = pendingFileRef.current;
    const pendingCredential = pendingCredentialRef.current;
    setPendingMethod(null);
    setShowCaptcha(false);

    if (method === "upload") {
      pendingFileRef.current = null;
      if (pendingFile) {
        onFileSelected(pendingFile, token);
      }
      return;
    }

    if (method === "credential") {
      pendingCredentialRef.current = null;
      if (pendingCredential) {
        onCredentialSubmit(pendingCredential, token);
      }
      return;
    }

    onMethodSelect(method, token);
    captchaTokenRef.current = null;
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (captchaRequired) {
      pendingFileRef.current = file;
      setPendingMethod("upload");
      setShowCaptcha(true);
      return;
    }

    onFileSelected(file, null);
  };

  const handleCredentialSubmit = (event) => {
    event.preventDefault();
    const value = credentialId.trim();
    if (!value) return;

    if (captchaRequired) {
      pendingCredentialRef.current = value;
      setPendingMethod("credential");
      setShowCaptcha(true);
      return;
    }

    onCredentialSubmit(value, null);
  };

  if (isLoading) {
    return <PublicPortalVerificationMethodsSkeleton />;
  }

  return (
    <section className="w-full px-5 pb-14 pt-4 sm:px-8 sm:pt-6 lg:px-12 lg:pt-8">
      <h2 className="mb-7 text-center text-2xl font-bold text-cfHeadingText">
        {t("methods.title")}
      </h2>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {methods.map((method) => {
          const Icon = methodIcons[method.icon];
          return (
            <article
              className="flex min-h-[280px] w-full min-w-0 flex-col items-center overflow-hidden rounded-2xl border border-cfCardBorder bg-cfSecondary p-5 text-center shadow-sm transition hover:-translate-y-1 hover:border-cfPrimary hover:shadow-md sm:p-7"
              key={method.id}
            >
              <span className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-cfSearchContainer text-cfPrimary">
                <Icon aria-hidden="true" size={27} />
              </span>
              <h3 className="mb-3 text-lg font-bold text-cfHeadingText">
                {method.title}
              </h3>
              <p className="mb-6 flex-1 text-sm leading-6 text-cfGlobalText">
                {method.description}
              </p>
              {method.id === "credential" ? (
                <div className="w-full min-w-0">
                  {!showCredentialInput && (
                    <button
                      className="inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-[8px] border border-cfPrimary bg-cfPrimary px-5 py-3 text-sm font-semibold text-publicPortal-onBrand transition hover:border-cfPrimaryDark hover:bg-cfPrimaryDark"
                      type="button"
                      onClick={() => handleMethodClick(method.id)}
                    >
                      <CreditCard aria-hidden="true" size={17} />
                      {method.action}
                    </button>
                  )}

                  {showCredentialInput && (
                    <form
                      className="mt-4 flex w-full min-w-0 flex-col items-stretch overflow-hidden rounded-[8px] border border-cfCardBorder bg-cfSecondary focus-within:border-cfPrimary sm:flex-row"
                      onSubmit={handleCredentialSubmit}
                    >
                      <input
                        className="min-w-0 flex-1 border-0 bg-transparent px-3 py-3 text-sm text-cfHeadingText outline-none focus:outline-none focus:ring-0 sm:px-4"
                        value={credentialId}
                        onChange={(event) =>
                          setCredentialId(event.target.value)
                        }
                        placeholder={t("verification.credentialPlaceholder")}
                        aria-label={t("methods.credential.title")}
                        autoFocus
                      />
                      <button
                        className="shrink-0 border-0 bg-cfPrimary px-3 py-3 text-sm font-semibold text-publicPortal-onBrand transition hover:bg-cfPrimaryDark disabled:cursor-not-allowed disabled:opacity-50 sm:px-5"
                        type="submit"
                        disabled={!credentialId.trim()}
                      >
                        {method.actionButton}
                      </button>
                    </form>
                  )}
                </div>
              ) : (
                <button
                  className="inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-[8px] border border-cfPrimary bg-cfPrimary px-5 py-3 text-sm font-semibold text-publicPortal-onBrand transition hover:border-cfPrimaryDark hover:bg-cfPrimaryDark focus:outline-none focus:ring-2 focus:ring-cfPrimaryLight"
                  type="button"
                  onClick={() => handleMethodClick(method.id)}
                >
                  {method.id === "scan" && (
                    <Camera aria-hidden="true" size={17} />
                  )}
                  {method.id === "upload" && (
                    <Upload aria-hidden="true" size={17} />
                  )}
                  {method.action}
                </button>
              )}
            </article>
          );
        })}
      </div>

      <input
        ref={fileInputRef}
        className="hidden"
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        aria-label={t("methods.upload.inputLabel")}
        onChange={handleFileChange}
      />
      <p className="mb-0 mt-5 break-words px-1 text-center text-xs text-cfGlobalText">
        {t("methods.formats")}
      </p>

      {showCaptcha && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-publicPortal-overlay p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={t("verification.captchaTitle")}
        >
          <div className="relative w-full max-w-md rounded-2xl bg-cfSecondary p-6 text-center shadow-xl">
            <button
              type="button"
              className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-cfSearchContainer text-cfGlobalText"
              onClick={clearCaptchaState}
              aria-label={t("verification.close")}
            >
              <X aria-hidden="true" size={20} />
            </button>
            <h3 className="mb-3 pr-8 text-xl font-bold text-cfHeadingText">
              {t("verification.captchaTitle")}
            </h3>
            <p className="mb-5 text-sm text-cfGlobalText">
              {t("verification.captchaDescription")}
            </p>
            <div className="flex justify-center overflow-x-auto">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={AppConstants.GOOGLE_CAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
                onExpired={() => {
                  captchaTokenRef.current = null;
                }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default VerificationMethods;
